/**
 * React convenience for subscribing to remix commands for the lifetime of a
 * component. Ref-backed so the handler can close over fresh props/state without
 * re-subscribing each render (subscribe once, never stale).
 *
 * Typical use: a small component mounted near the remix surface that reacts to
 * commands — e.g. open chat on `RemixErrored`, toggle a plugin on
 * `RemixStarted`/`RemixGenerationEnded`/`RemixFlowEnded`.
 *
 * Additive; not wired into live flows.
 */

import { useEffect, useRef } from 'react';

import { registerRemixCommandHandler, type RemixCommandHandler } from './remixCommandBus';

export const useRemixCommands = (handler: RemixCommandHandler): void => {
	const ref = useRef(handler);
	ref.current = handler;

	useEffect(() => {
		// Stable proxy dispatching to the latest handler via the ref, so the
		// subscription is created once yet never goes stale.
		return registerRemixCommandHandler((command) => ref.current(command));
	}, []);
};
